import { useCallback, useMemo, useState } from "react";

import {
  findCompetitorItemById,
  findCompetitorItemIndexById,
  getCompetitorItemId,
  isCompetitorItemSelectable,
} from "../utils";

export const useCompetitorsSelection = ({
  galleryDisplayData,
  acceptedReferenceMediaType = null,
}) => {
  const [selectedItemId, setSelectedItemId] = useState(null);

  const resetSelection = useCallback(() => {
    setSelectedItemId(null);
  }, []);

  const onSelectItem = useCallback(
    (index, mediaOverride) => {
      const item = galleryDisplayData[index];
      const candidate = mediaOverride?.type
        ? { ...item, type: mediaOverride.type }
        : item;
      if (
        !isCompetitorItemSelectable(candidate, { acceptedReferenceMediaType })
      ) {
        return;
      }

      const itemId = getCompetitorItemId(item);
      setSelectedItemId((current) => (current === itemId ? null : itemId));
    },
    [acceptedReferenceMediaType, galleryDisplayData]
  );

  const selectedGalleryIndexes = useMemo(() => {
    if (!selectedItemId) return [];
    const index = findCompetitorItemIndexById({
      items: galleryDisplayData,
      itemId: selectedItemId,
    });
    return index >= 0 ? [index] : [];
  }, [galleryDisplayData, selectedItemId]);

  const selectedItem = useMemo(
    () =>
      findCompetitorItemById({
        items: galleryDisplayData,
        itemId: selectedItemId,
      }),
    [galleryDisplayData, selectedItemId]
  );

  const getSelectedItems = useCallback(
    (indexes) => {
      if (indexes) {
        return indexes
          .map((index) => galleryDisplayData[index])
          .filter(Boolean);
      }

      return selectedItem ? [selectedItem] : [];
    },
    [galleryDisplayData, selectedItem]
  );

  return {
    selectedItemId,
    selectedGalleryIndexes,
    selectedItem,
    resetSelection,
    onSelectItem,
    getSelectedItems,
  };
};

export default useCompetitorsSelection;
